import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
  draggable,
  dropTargetForElements
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import {
  attachClosestEdge,
  extractClosestEdge
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { type Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/types';
import { DropIndicator } from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box-without-terminal';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import SelectFieldMenu, {
  type SelectFieldMenuHandle
} from '@databases/context-menu/select-field-menu';
import { useContextualMenu } from '@databases/contextual-menu/useContextualMenu';
import {
  AiSummaryFieldHeaderAction,
  hasAiSummaryHeaderRefresh,
  useIsFieldScopedAiStreaming
} from '@databases/database-ai-fields';
import {
  hasUserEditedAiSummaryCells,
  runAiSummaryField,
  runManualAiSummaryRefresh
} from '@databases/database-ai-fields/run-ai-summary-field';
import { useIsAiFieldRunActive } from '@databases/database-ai-fields/use-is-ai-field-run-active';
import { aiFirstCreationStreamingState } from '@databases/database-ai-first-creation-state';
import {
  BORDER_BLUE,
  BORDER_GREEN,
  BORDER_ORANGE,
  BORDER_PURPLE,
  useDatabaseStreamState
} from '@databases/database-ai-first-creation-ui';
import { DragType, setPublicDragData } from '@databases/drag';
import FieldNameWithActions, {
  type FieldNameWithActionsProps
} from '@databases/field-name/FieldNameWithActions';
import Z_INDEX from '@databases/layout/z-index';
import { useDatabaseSelectionPing } from '@databases/ping-registry';
import {
  getPinnedLeadingFieldCountAfterFieldsInserted,
  getPinnedLeadingFieldCountAfterFieldsRemoved
} from '@databases/pinned-columns/compute-pinned-leading-layout';
import { usePinnedLeadingColumns } from '@databases/pinned-columns/pinned-leading-columns-context';
import {
  getPinnedLeadingHeaderCellStyle,
  isPinnedLeadingDropIndicatorOccluded,
  PINNED_LEADING_BOUNDARY_DROP_TARGET_ACTIVE_STYLE,
  PINNED_LEADING_BOUNDARY_DROP_TARGET_INACTIVE_STYLE,
  PINNED_LEADING_BOUNDARY_SELECTOR,
  PINNED_LEADING_DROP_INDICATOR_WIDTH_PX
} from '@databases/pinned-columns/pinned-leading-header';
import {
  getPinnedLeadingBoundaryCellShadowStyle,
  getPinnedLeadingHeaderSurfaceGapCoverStyle
} from '@databases/pinned-columns/pinned-leading-shadow';
import { setPinnedLeadingFieldCountInProperties } from '@databases/pinned-columns/pinned-leading-view-properties';
import { PresenceNameplate, usePresence } from '@databases/presence';
import { getGridTemplateColumns } from '@databases/quick-calc/utils';
import { isModifierKeyPressed, useHoverState } from '@databases/react';
import { type DefaultRootState, defaultStoreUtils } from '@databases/redux';
import {
  selectIsEntireFieldSelected,
  selectIsHoverDeleteFieldsButton,
  selectIsSoftSelectEnabled,
  selectSelectedAdjacentCells
} from '@databases/redux/cellSelections/selectors';
import { calculateRangeFromFocus } from '@databases/redux/cellSelections/utils';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { getDatabaseWithCurrentView } from '@databases/redux/database-view';
import { getService } from '@databases/service-container';
import {
  type AiSummaryFieldConfig,
  type Database,
  DatabaseFieldType
} from '@databases/types/database';
import { DatabaseChangeType } from '@databases/types/redux';
import { TABLE_CLASSNAMES } from '@databases/utils/constants';
import { blurActiveElement, removeTextSelection } from '@databases/utils/database/utils';
import {
  type ViewChangeType,
  ViewChangeTypes
} from '@databases/utils/database/view/view';
import { useIsFeatureEnabled } from '@databases/utils/hooks/useIsFeatureEnabled';
import useView from '@databases/views/useView';
import composeRefs from '@seznam/compose-react-refs';
import classNames from 'classnames';
import React, {
  type CSSProperties,
  type ForwardedRef,
  forwardRef,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useIntl } from 'react-intl-next';
import styled, { css } from 'styled-components';
import { useSnapshot } from 'valtio';

import { getExperimentValue } from '@canvas/client-feature-flags';

import {
  getCellsFromRow,
  getRowFromCell,
  getRowsFromBody,
  getTableFromBodyRow
} from '../../../table.ts';
import { isApple } from '../../../utils.ts';
import { useDatabaseAiConfigModal } from '../ai-config/database-ai-config-modal-provider.tsx';

import { type DragState } from './TableLayoutRow.tsx';
import { buildFieldDragImageFromState } from './build-field-drag-image-from-state.tsx';
import { getAiFieldStreamingParticipants } from './get-ai-field-streaming-participants.ts';

const SELECT_BUTTON_HEIGHT = 14;
type AiSummaryRerunOperation = 'refresh-entire-column' | 'refresh-empty-entries';

const HEADER_INTERACTIVE_TARGET_SELECTOR = [
  '[data-contextual-menu="true"]',
  '.menu-portal-target',
  '[role="listbox"]',
  '[role="option"]',
  ':is(button, input, textarea, select, a, [contenteditable="true"])'
].join(', ');

const DropIndicatorStyling = css<{ containerHeight: number }>`
  ::after {
    content: ' ';
    position: absolute;
    opacity: 1;
    top: ${token('space.negative.050', '-4px')};
    left: ${token('space.negative.025', '-2px')};
    width: ${PINNED_LEADING_DROP_INDICATOR_WIDTH_PX}px;
    height: ${({ containerHeight }) => `${containerHeight - 8}px`};
    border-radius: ${token('radius.xsmall', '2px')};
    border: none;
    background-color: ${colors.B200};
    transition: opacity 0.2s;
    z-index: ${Z_INDEX.TABLE_VIEW_DROP_INDICATOR};
  }
`;

const PragmaticDropIndicatorStyling = styled.div<{ containerHeight: number }>`
  content: ' ';
  position: absolute;
  opacity: 1;
  left: ${token('space.negative.025', '-2px')};
  height: ${({ containerHeight }) => `${containerHeight - 8}px`};
  border-radius: ${token('radius.xsmall', '2px')};
  border: none;
  background-color: ${colors.B200};
  transition: opacity 0.2s;
  z-index: ${Z_INDEX.TABLE_VIEW_DROP_INDICATOR};
`;

// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
const PragmaticDropIndicatorStylingLastInRow = styled.div<{ containerHeight: number }>`
  ::after {
    content: ' ';
    position: absolute;
    opacity: 1;
    top: ${token('space.0', '0px')};
    width: 2px;
    right: -1px;
    height: ${({ containerHeight }) => `${containerHeight - 8}px`};
    border-radius: ${token('radius.xsmall', '2px')};
    border: none;
    background-color: ${token('color.border.selected', '#0C66E4')};
    transition: opacity 0.2s;
    z-index: ${Z_INDEX.TABLE_VIEW_DROP_INDICATOR};
  }
`;

export const Th = styled.th<{
  isDragOver: boolean;
  containerHeight: number;
  isFirstInRow: boolean;
  isSelectedFromPing?: boolean;
  isFirstOfSelection?: boolean;
  isLastOfSelection?: boolean;
  collaboratorColor?: string;
}>`
  position: relative;
  overflow: visible;

  ${({ isSelectedFromPing, isFirstOfSelection, isLastOfSelection, isFirstInRow }) =>
    isSelectedFromPing &&
    css`
      &::after {
        content: '';
        position: absolute;
        // Extend left by 1px on non-first-in-row columns so the ::after aligns with the
        // body highlight overlay which measures from the outer border-box edge (including
        // the cell's 1px left border) via getBoundingClientRect().
        inset: 0 0 -1px ${isFirstInRow ? '0px' : '-1px'};
        background: color-mix(
          in srgb,
          ${token('color.background.accent.blue.subtlest', '#e9f2ff')} 25%,
          transparent
        );
        box-sizing: border-box;
        border-top: 2px solid ${token('color.border.selected', '#0c66e4')};
        border-bottom: none;
        border-left: ${isFirstOfSelection
          ? `2px solid ${token('color.border.selected', '#0c66e4')}`
          : 'none'};
        border-right: ${isLastOfSelection
          ? `2px solid ${token('color.border.selected', '#0c66e4')}`
          : 'none'};
        border-radius: ${isFirstOfSelection ? token('radius.small', '3px') : '0'}
          ${isLastOfSelection ? token('radius.small', '3px') : '0'} 0 0;
        pointer-events: none;
        z-index: 1;
      }

      .highlight-delete-intent &::after {
        background: color-mix(
          in srgb,
          ${token('color.background.danger.hovered', '#ffd5d2')} 25%,
          transparent
        );
        border-top-color: ${token('color.border.danger', '#e34935')};
        border-left-color: ${token('color.border.danger', '#e34935')};
        border-right-color: ${token('color.border.danger', '#e34935')};
      }
    `}
  --database-rovo-gradient-angle: 0deg;
  --column-height: 0px;
  --column-left-inset: 0px;
  &.${TABLE_CLASSNAMES.SHOW_AS_DISABLED} {
    * {
      cursor: ${({ isFilterOrSortingActive }) =>
        isFilterOrSortingActive ? 'not-allowed' : 'grabbing'} !important;
    }
  }

  background: ${token('elevation.surface.sunken')};

  &:hover:not(.${TABLE_CLASSNAMES.SHOW_AS_DISABLED}) {
    .${TABLE_CLASSNAMES.SELECT_BUTTON} {
      display: flex;
    }
  }

  ${({ isDragOver }) => isDragOver && DropIndicatorStyling}
  // This maximizes the table header cell height to the height of the whole table body to be able to drag the column over the whole table
  &.${TABLE_CLASSNAMES.MAXIMIZE_HEIGHT} {
    ::before {
      content: '';
      position: absolute;
      left: 0;
      top: ${token('space.negative.100', '-8px')};
      height: ${({ containerHeight }) => `${containerHeight - 8}px`};
      width: 100%;
    }
  }

  &.${TABLE_CLASSNAMES.IS_ROVO_GRADIENT_SELECTED}::before {
    content: '';
    position: absolute;
    top: ${token('space.negative.025', '-2px')};
    left: ${token('space.negative.025', '-2px')};
    right: ${token('space.negative.025', '-2px')};
    height: var(--column-height, 0px);
    border-radius: ${token('radius.small', '3px')};
    padding: ${token('space.025', '2px')};
    background: conic-gradient(
      from var(--database-rovo-gradient-angle, 0deg),
      ${BORDER_ORANGE} 25%,
      ${BORDER_GREEN} 25% 50%,
      ${BORDER_BLUE} 50% 75%,
      ${BORDER_PURPLE} 75% 100%
    );
    -webkit-mask:
      linear-gradient(#000 0 0) content-box,
      linear-gradient(#000 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
    z-index: 1;
  }

  ${({ collaboratorColor }) => {
    // eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
    const borderColor = collaboratorColor && token(collaboratorColor);

    return (
      borderColor &&
      css`
        border: ${token('border.width.selected', '2px')} solid ${borderColor};
        border-radius: ${token('radius.small', '3px')};
      `
    );
  }}
`;

type TableLayoutHeaderCellRefObj = RefObject<
  TableLayoutHeaderCellRefs<
    RefObject<HTMLDivElement | null>,
    RefObject<HTMLDivElement | null>
  >
>;

type TableLayoutHeaderCellRefs<H, T> = {
  headerFieldRef: H;
  tableBodyRef: T;
};

export interface TableLayoutHeaderCellProps extends FieldNameWithActionsProps {
  canEditFields: boolean;
  couldEditFields: boolean;
  className?: string;
  currentViewId: string;
  isLastInRow?: boolean;
  onDragStart?: (e: React.DragEvent, entryId: string) => void;
  onDrag?: (e: React.DragEvent, entryId: string) => void;
  onDragEnd?: (e: React.DragEvent, entryId: string) => void;
  isDraggingActive?: boolean;
  isDragOver: boolean;
  containerHeight: number;
  visibleTableContentHeight: number;
  fieldIndex: number;
  visibleFieldIds: ReadonlyArray<string>;
  fieldToScroll: string | null;
  setFieldToScroll: (fieldId: string | null) => void;
  /**
   * databasesFrontendHighlightScrollReliability control-arm switch: off, this component scrolls itself; on, TableLayoutHeaderRow does.
   */
  isScrollSettleFixEnabled: boolean;
}

/**
 * Renders a th element that contains the field name.
 * The field name can be dragged to reorder the fields.
 */
const TableLayoutHeaderCell = React.memo(
  forwardRef<
    TableLayoutHeaderCellRefs<
      ForwardedRef<HTMLElement | null>,
      ForwardedRef<HTMLTableSectionElement | null>
    >,
    TableLayoutHeaderCellProps
  >(
    (
      {
        canEditFields,
        contentId,
        fieldId,
        className,
        isLastInRow,
        onDragStart,
        onDrag,
        onDragEnd,
        isDraggingActive,
        containerHeight,
        visibleTableContentHeight,
        isDragOver,
        currentViewId,
        isFirstInRow,
        fieldIndex,
        visibleFieldIds,
        fieldToScroll,
        setFieldToScroll,
        isScrollSettleFixEnabled,
        ...otherProps
      },
      ref
    ) => {
      const { locale } = useIntl();
      const { view } = useView();
      const pinnedLeadingColumns = usePinnedLeadingColumns();
      const viewSettings = view.settings;
      const inputRef = useRef<HTMLInputElement>(null);
      const thRef = useRef<HTMLTableCellElement>(null);
      const pinnedLeadingBoundaryDropTargetRef = useRef<HTMLDivElement>(null);
      const pinnedLeadingBoundaryElementRef = useRef<HTMLElement | null>(null);
      const {
        enabled: isPinnedLeadingEnabled,
        layoutByFieldIndex,
        setPinnedLeadingFieldCount,
        pinnedLeadingFieldCount,
        maxPinnedLeadingFieldCount
      } = pinnedLeadingColumns;
      const pinnedLeadingLayout = isPinnedLeadingEnabled
        ? layoutByFieldIndex[fieldIndex]
        : undefined;
      const isPinnedLeadingFreezeBoundary = Boolean(
        pinnedLeadingLayout?.isFreezeBoundary
      );

      const { activeStream, isStreaming, operationType } = useDatabaseStreamState();
      const isDBAiFirstCreationEnabled = useIsFeatureEnabled('dbAiFirstCreation');
      const isEditStreaming = operationType === 'edit';
      const isFieldScopedAiStreaming = useIsFieldScopedAiStreaming(activeStream);
      const isAiFieldRunActive = useIsAiFieldRunActive({
        contentId,
        aiFieldType: 'summary',
        fieldId,
        activeStream
      });

      const { isSingleFieldStreaming } = useSnapshot(aiFirstCreationStreamingState);
      const isAIStreaming = isDBAiFirstCreationEnabled && isStreaming;
      const isFieldEditStreaming = isEditStreaming && Boolean(isSingleFieldStreaming);
      useEffect(() => {
        if (!isPinnedLeadingEnabled || pinnedLeadingFieldCount === 0) {
          pinnedLeadingBoundaryElementRef.current = null;
          return;
        }

        const boundaryElement = thRef.current?.ownerDocument.querySelector(
          PINNED_LEADING_BOUNDARY_SELECTOR
        );
        pinnedLeadingBoundaryElementRef.current =
          boundaryElement instanceof HTMLElement ? boundaryElement : null;
      }, [fieldIndex, isPinnedLeadingEnabled, pinnedLeadingFieldCount]);

      useEffect(() => {
        // Control arm: TableLayoutHeaderRow owns scrolling when this is on.
        if (isScrollSettleFixEnabled) {
          return;
        }

        if (isAIStreaming && !isEditStreaming) {
          setFieldToScroll(null);
          return;
        }

        if (isAIStreaming && isFieldEditStreaming) {
          setFieldToScroll(null);
          return;
        }

        if (fieldToScroll === fieldId && thRef?.current) {
          requestAnimationFrame(() => {
            thRef.current?.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'center'
            });
          });
        }
      }, [
        isScrollSettleFixEnabled,
        fieldId,
        fieldToScroll,
        setFieldToScroll,
        isEditStreaming,
        isFieldEditStreaming,
        isAIStreaming
      ]);

      const [isHoverDragButton, dragButtonHoverProps] = useHoverState();
      const isSelectionsPerformanceEnabled = getExperimentValue(
        'databasesSelectionsPerformance',
        'isEnabled',
        {
          fireExperimentExposure: false
        }
      );
      const isContextualMenusM2Enabled = getExperimentValue(
        'databasesContextualMenusM2',
        'enabled',
        {
          fireExperimentExposure: false
        }
      );

      const { participants, recentCollaboratorColor } = usePresence({
        grouping: { fieldId },
        componentId: 'TableLayoutHeaderCell',
        isEnabled: true
      });
      const displayParticipants = getAiFieldStreamingParticipants({
        participants,
        shouldExcludeAiAgent: isFieldScopedAiStreaming
      });

      const isFieldSelectedFromPing = useDatabaseSelectionPing((reader) =>
        reader.isFieldSelected(fieldId)
      );

      const isFieldFirstOfSelection = useDatabaseSelectionPing(() =>
        isSelectionsPerformanceEnabled
          ? getService('SelectionsApi').isFieldFirstOfRange(fieldIndex)
          : false
      );

      const isFieldLastOfSelection = useDatabaseSelectionPing(() =>
        isSelectionsPerformanceEnabled
          ? getService('SelectionsApi').isFieldLastOfRange(fieldIndex)
          : false
      );

      const field = getService('DatabaseSdk').fields.getFieldById(fieldId);
      const aiSummaryField =
        field && hasAiSummaryHeaderRefresh(field.type) ? field : null;

      const createAiSummaryRerunHandler = useCallback(
        (operation: AiSummaryRerunOperation) => () => {
          if (!aiSummaryField) {
            return;
          }

          void runManualAiSummaryRefresh({
            contentId,
            fieldId,
            targetFieldName: aiSummaryField.name,
            config:
              aiSummaryField.settings.aiFieldConfig?.aiFieldType === 'summary'
                ? aiSummaryField.settings.aiFieldConfig.field
                : { outputFormat: 'paragraph' },
            locale,
            viewId: currentViewId,
            operation,
            triggerType: 'fullColumnRegenerate',
            runScope:
              operation === 'refresh-empty-entries' ? 'emptyCellsOnly' : 'entireColumn',
            source: 'databaseView'
          }).catch(() => undefined);
        },
        [aiSummaryField, contentId, currentViewId, fieldId, locale]
      );

      const handleAiSummaryRerunEntireColumn = useMemo(
        () => createAiSummaryRerunHandler('refresh-entire-column'),
        [createAiSummaryRerunHandler]
      );
      const handleAiSummaryRerunEmptyEntries = useMemo(
        () => createAiSummaryRerunHandler('refresh-empty-entries'),
        [createAiSummaryRerunHandler]
      );

      const aiFieldLastRunAt = canEditFields
        ? aiSummaryField?.aiRunMetadata?.lastRunAt
        : undefined;

      // Legacy redux selection state — used only for old styled-component styling in child components.
      // Will be removed once the selections package migration (databasesSelectionsPerformance) is complete.
      const [
        isLegacySelectedFromRedux,
        isLegacySoftSelected,
        legacyHoverDeleteButton,
        legacySelectedAdjacentCells
      ] = defaultStoreUtils.useEqualSelector((state: DefaultRootState) => {
        const isSoftSelected = selectIsSoftSelectEnabled(state);

        const isSelected = selectIsEntireFieldSelected(state, {
          fieldId
        });

        return [
          isSelected,
          isSoftSelected,
          isSelected && selectIsHoverDeleteFieldsButton(state),
          isSelected
            ? selectSelectedAdjacentCells(state, {
                fieldId,
                contentId
              })
            : undefined
        ] as const;
      });

      const isSelected = isSelectionsPerformanceEnabled
        ? isFieldSelectedFromPing
        : isLegacySelectedFromRedux;

      const store = defaultStoreUtils.useStore();
      const dispatch = defaultStoreUtils.useDispatch();
      const getHasUserEditedAiSummaryCells = useCallback(() => {
        const database = getService('DatabaseSdk').toJSON() as Pick<
          Database,
          'entries' | 'entriesById'
        >;

        return hasUserEditedAiSummaryCells({ database, fieldId });
      }, [fieldId]);
      const { openAiConfigModal } = useDatabaseAiConfigModal();
      const selectionsActions = getService('SelectionsActions');
      const selectionsReader = getService('SelectionsReader');
      const fieldMenuRef = useRef<SelectFieldMenuHandle>(null);

      const handleEditAiSummaryField = useCallback(() => {
        const field = getService('DatabaseSdk').fields.getFieldById(fieldId);

        if (field?.type !== DatabaseFieldType.AI_SUMMARY) {
          return;
        }

        const persistConfig = (config: AiSummaryFieldConfig) => {
          dispatch(
            addChangeToDraft(contentId, {
              type: DatabaseChangeType.SET_AI_FIELD_CONFIG,
              fieldId,
              aiFieldConfig: {
                aiFieldType: 'summary',
                field: config
              }
            })
          );
        };
        const initialConfig =
          field.settings.aiFieldConfig?.aiFieldType === 'summary'
            ? field.settings.aiFieldConfig.field
            : undefined;

        openAiConfigModal({
          type: 'ai-summary-field',
          props: {
            fieldId,
            fieldName: field.name,
            mode: 'edit',
            openedFrom: 'editFieldDropdown',
            initialConfig,
            onSaveAndRun: (config) => {
              persistConfig(config);
              return runAiSummaryField({
                contentId,
                fieldId,
                targetFieldName: field.name,
                config,
                locale,
                viewId: currentViewId,
                operation: 'refresh-entire-column',
                triggerType: 'saveAndRun',
                runScope: 'entireColumn',
                source: 'aiFieldModal'
              });
            },
            onSaveWithoutRunning: persistConfig
          }
        });
      }, [contentId, currentViewId, dispatch, fieldId, locale, openAiConfigModal]);

      /**
       * Selection logic from Redux thunks `selectFieldSingle` and `selectFieldMulti`.
       *
       * - No modifier: selectFieldSingle — toggle: if only selected field, deselect; else replace selection
       * - Shift: selectFieldMulti(isRangeSelect=true) — range select, adds to existing
       * - Shift+Meta/Ctrl: selectFieldMulti(isRangeSelect=false) — toggle individual field in/out of selection
       */
      const handleSelectColumn = useCallback(
        (e: React.MouseEvent | React.KeyboardEvent) => {
          const isMultiSelectOn = isModifierKeyPressed(e, [
            isApple ? 'metaKey' : 'ctrlKey',
            'shiftKey'
          ]);
          const isRangeSelectOn = isModifierKeyPressed(e, ['shiftKey']);
          blurActiveElement();
          removeTextSelection();

          const reader = getService('SelectionsReader');

          if (isRangeSelectOn) {
            if (!isMultiSelectOn) {
              const focusedItem = reader.getFocusedItem();
              const database = getDatabaseWithCurrentView(store.getState(), {
                contentId
              });
              const fields = database?.fields ?? [];
              const focusedFieldId =
                focusedItem && 'fieldId' in focusedItem ? focusedItem.fieldId : undefined;
              const rangeFieldIds = calculateRangeFromFocus(
                fields,
                focusedFieldId,
                fieldId
              );

              selectionsActions.selectFields(rangeFieldIds, { clear: false });
            } else {
              const isSelected = reader.isFieldSelected(fieldId);

              if (isSelected) {
                const selectedFieldIds = reader.getSelectedFieldIds();
                const remaining = selectedFieldIds.filter((id) => id !== fieldId);

                if (remaining.length > 0) {
                  selectionsActions.selectFields(remaining, {
                    clearSelectedValues: false
                  });
                } else {
                  selectionsActions.deselectAll();
                }
              } else {
                selectionsActions.selectFields([fieldId], { clear: false });
              }
            }
          } else {
            const selectedFieldIds = reader.getSelectedFieldIds() ?? [];

            if (selectedFieldIds.length === 1 && selectedFieldIds[0] === fieldId) {
              selectionsActions.deselectAll();
            } else {
              selectionsActions.selectFields([fieldId]);
            }
          }

          selectionsActions.clearSelectionIntent();
        },
        [contentId, store, selectionsActions, fieldId]
      );

      const { closeMenu } = useContextualMenu();
      const isHeaderInteractiveTarget = useCallback((target: EventTarget | null) => {
        if (!(target instanceof Element)) {
          return false;
        }

        return target.closest(HEADER_INTERACTIVE_TARGET_SELECTOR) !== null;
      }, []);

      const handleHeaderClickCapture = useCallback(
        (e: React.MouseEvent) => {
          if (!isContextualMenusM2Enabled || !canEditFields) {
            return;
          }

          if (isHeaderInteractiveTarget(e.target)) {
            return;
          }

          e.preventDefault();
          e.stopPropagation();
          closeMenu();
          handleSelectColumn(e);
        },
        [
          canEditFields,
          closeMenu,
          handleSelectColumn,
          isHeaderInteractiveTarget,
          isContextualMenusM2Enabled
        ]
      );

      const handleHeaderContextMenu = useCallback(
        (e: React.MouseEvent) => {
          if (!isContextualMenusM2Enabled || !canEditFields) {
            return;
          }

          if (isHeaderInteractiveTarget(e.target)) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }

          e.preventDefault();
          e.stopPropagation();

          void fieldMenuRef.current?.openAtPosition({ x: e.clientX, y: e.clientY });
        },
        [canEditFields, isContextualMenusM2Enabled, isHeaderInteractiveTarget]
      );

      const [dragState, setDragState] = useState<DragState>({ type: 'idle' });
      const showAsDisabled = isSelected && dragState.type === 'dragging';

      const dragImage = useRef<HTMLElement>();

      /**
       * Legacy drag image builder — finds selected columns via IS_SELECTED CSS class,
       * clones the header row and tbody and filters.
       */
      const buildFieldDragImageLegacy = useCallback(
        (e: React.DragEvent) => {
          // do not apply any styles to these references of elements or use it directly for the drag image
          const tr = getRowFromCell(thRef.current);
          const table = getTableFromBodyRow(tr);
          const tbody = (ref as TableLayoutHeaderCellRefObj)?.current?.tableBodyRef
            .current as HTMLTableSectionElement | null;
          const trs = getRowsFromBody(tbody);
          const ths = getCellsFromRow(tr);
          const selectedThs = ths.filter((el) =>
            el.classList.contains(TABLE_CLASSNAMES.IS_SELECTED)
          );
          const currentIndexInSelectedThs = selectedThs.indexOf(thRef.current!);
          const selectedThsIndexOffsetWidth = selectedThs
            .slice(0, currentIndexInSelectedThs + 1)
            .reduce((p, c) => p + c.clientWidth, 0);
          const selectedThsWidths = selectedThs.map((el) => el.clientWidth);
          const selectedThsWidth = selectedThsWidths.reduce((p, c) => p + c, 0);

          dragImage.current = document.createElement('div');
          const newThead = document.createElement('thead');
          const newTBody = document.createElement('tbody');
          const newTr = document.createElement('tr');

          // table header copy
          const trClone = tr?.cloneNode(true) as HTMLTableRowElement | null;
          const clonedThs = getCellsFromRow(trClone);
          const clonedSelectedThs = clonedThs.filter((el) =>
            el.classList.contains(TABLE_CLASSNAMES.IS_SELECTED)
          );

          // select handle
          const selectButton = clonedSelectedThs
            .find((el) => el.draggable)
            ?.querySelector(`.${TABLE_CLASSNAMES.SELECT_BUTTON}`) as HTMLElement | null;
          selectButton?.classList.add(TABLE_CLASSNAMES.SHOW_AS_DRAGGING);

          // table body copy
          const tbodyClone = tbody?.cloneNode(true) as HTMLTableSectionElement | null;
          const clonedTrs = getRowsFromBody(tbodyClone);
          clonedTrs?.forEach((el) => {
            const trCopy = newTr.cloneNode(false) as HTMLTableRowElement;
            const clonedThs = getCellsFromRow(el);
            const selectedClonedThs = clonedThs.filter((el) =>
              el.classList.contains(TABLE_CLASSNAMES.IS_SELECTED)
            );
            // eslint-disable-next-line canvas/no-spread-arguments
            trCopy.append(...selectedClonedThs);
            newTBody.append(trCopy);
            Object.assign(trCopy.style, {
              display: 'grid',
              gridTemplateColumns: getGridTemplateColumns(selectedThsWidths)
            });
          });

          // show selected cells as disabled in table body
          trs?.forEach((el) => {
            const ths = getCellsFromRow(el);
            const selectedThs = ths.filter((el) =>
              el.classList.contains(TABLE_CLASSNAMES.IS_SELECTED)
            );
            selectedThs.forEach((el) =>
              el.classList.add(TABLE_CLASSNAMES.SHOW_AS_DISABLED)
            );
          });

          // clone the html table element to get the styles (but do not include descendants)
          const tableClone = table?.cloneNode(false) as HTMLTableElement | null;
          if (tableClone) {
            Object.assign(dragImage.current.style, {
              position: 'absolute',
              top: 0,
              left: 0,
              zIndex: Z_INDEX.DRAG_IMAGE_ELEMENT,
              paddingTop: 0
            });
            Object.assign(tableClone.style, {
              minWidth: `${selectedThsWidth}px`
            });
            Object.assign(newTr.style, {
              display: 'grid',
              gridTemplateColumns: getGridTemplateColumns(selectedThsWidths)
            });

            tableClone.append(newThead);
            tableClone.append(newTBody);
            // eslint-disable-next-line canvas/no-spread-arguments
            newTr.append(...clonedSelectedThs);
            newThead.append(newTr);
            dragImage.current.append(tableClone);
            table?.parentElement?.append(dragImage.current);

            e.dataTransfer.setDragImage(
              dragImage.current,
              selectedThsIndexOffsetWidth - (e.target as HTMLElement)?.clientWidth / 2,
              SELECT_BUTTON_HEIGHT / 2
            );
          }
        },
        [ref]
      );

      const handleDragStart = useCallback(
        (e: React.DragEvent) => {
          if (e.target !== e.currentTarget || !thRef.current) {
            // Ignore dragging descendent elements
            return;
          }
          removeTextSelection();
          closeMenu();

          const selectedFieldIds = selectionsReader.getSelectedFieldIds();

          if (isSelectionsPerformanceEnabled) {
            buildFieldDragImageFromState({
              e,
              selectedFieldIds,
              thRef: thRef as React.RefObject<HTMLTableCellElement>,
              tableBodyRef: (ref as TableLayoutHeaderCellRefObj)?.current
                ?.tableBodyRef as React.RefObject<HTMLTableSectionElement | null>,
              dragImageRef: dragImage
            });
          } else {
            buildFieldDragImageLegacy(e);
          }

          setPublicDragData(e.dataTransfer, {
            type: DragType.FIELD_COLUMNS,
            contentId,
            draggingIds: selectedFieldIds,
            dragOverId: fieldId,
            height: thRef.current!.offsetHeight,
            width: thRef.current!.offsetWidth
          });

          onDragStart?.(e, fieldId);
        },
        [
          buildFieldDragImageLegacy,
          closeMenu,
          contentId,
          fieldId,
          isSelectionsPerformanceEnabled,
          onDragStart,
          ref,
          selectionsReader
        ]
      );

      const handleDrag = useCallback(
        (e: React.DragEvent) => {
          if (e.target !== e.currentTarget) {
            // Ignore dragging descendent elements
            return;
          }

          onDrag?.(e, fieldId);
        },
        [fieldId, onDrag]
      );

      const handleDragEnd = useCallback(
        (e: React.DragEvent) => {
          if (e.target !== e.currentTarget) {
            // Ignore dragging descendent elements
            return;
          }

          if (dragImage.current) {
            dragImage.current.remove();
            dragImage.current = undefined;
          }

          const tbody = (ref as TableLayoutHeaderCellRefObj)?.current?.tableBodyRef
            .current as HTMLTableSectionElement | null;
          const trs = getRowsFromBody(tbody);
          // show selected cells as disabled in table body
          trs.forEach((el) => {
            const ths = getCellsFromRow(el);
            const selectedThs = ths.filter((el) =>
              el.classList.contains(TABLE_CLASSNAMES.IS_SELECTED)
            );
            selectedThs.forEach((el) =>
              el.classList.remove(TABLE_CLASSNAMES.SHOW_AS_DISABLED)
            );
          });

          onDragEnd?.(e, fieldId);
        },
        [fieldId, onDragEnd, ref]
      );

      // Pragmatic drag and drop
      useEffect(() => {
        if (!thRef.current) {
          return;
        }
        const element = thRef.current;
        const boundaryDropTargetElement = pinnedLeadingBoundaryDropTargetRef.current;

        const registrations = [
          draggable({
            element,
            getInitialData() {
              return { type: DragType.FIELD_COLUMNS, fieldIndex, fieldId };
            },
            onDragStart() {
              setDragState({ type: 'dragging' });
            },
            onDrop() {
              setDragState({ type: 'idle' });
            }
          }),
          dropTargetForElements({
            element,
            getData({ input, element }) {
              const data = { fieldId, fieldIndex };
              return attachClosestEdge(data, {
                input,
                element,
                allowedEdges: ['left', 'right']
              });
            },
            canDrop({ source }) {
              return (
                (source.data.type === DragType.FIELD_COLUMNS &&
                  source.data.fieldId !== fieldId) ||
                source.data.type === DragType.FILL_VALUES
              );
            },
            onDragEnter(args) {
              if (args.source.data.type === DragType.FIELD_COLUMNS) {
                setDragState({
                  type: 'is-over',
                  closestEdge: extractClosestEdge(args.self.data)
                });
              }
            },
            onDrag(args) {
              if (args.source.data.type === DragType.FIELD_COLUMNS) {
                const closestEdge: Edge | null = extractClosestEdge(args.self.data);

                // only update react state if the `closestEdge` changes
                setDragState((current) => {
                  if (current.type !== 'is-over') {
                    return current;
                  }
                  if (current.closestEdge === closestEdge) {
                    return current;
                  }
                  return {
                    type: 'is-over',
                    closestEdge
                  };
                });
              }
            },
            onDragLeave() {
              setDragState({ type: 'idle' });
            },
            onDrop() {
              setDragState({ type: 'idle' });
            }
          })
        ];

        if (isPinnedLeadingFreezeBoundary && boundaryDropTargetElement) {
          registrations.push(
            dropTargetForElements({
              element: boundaryDropTargetElement,
              getData({ input, element }) {
                const data = { fieldId, fieldIndex };
                return attachClosestEdge(data, {
                  input,
                  element,
                  allowedEdges: ['right']
                });
              },
              canDrop({ source }) {
                return (
                  (source.data.type === DragType.FIELD_COLUMNS &&
                    source.data.fieldId !== fieldId) ||
                  source.data.type === DragType.FILL_VALUES
                );
              },
              onDragEnter(args) {
                if (args.source.data.type === DragType.FIELD_COLUMNS) {
                  setDragState({
                    type: 'is-over',
                    closestEdge: extractClosestEdge(args.self.data)
                  });
                }
              },
              onDrag(args) {
                if (args.source.data.type === DragType.FIELD_COLUMNS) {
                  const closestEdge: Edge | null = extractClosestEdge(args.self.data);

                  setDragState((current) => {
                    if (current.type !== 'is-over') {
                      return current;
                    }
                    if (current.closestEdge === closestEdge) {
                      return current;
                    }
                    return {
                      type: 'is-over',
                      closestEdge
                    };
                  });
                }
              },
              onDragLeave() {
                setDragState({ type: 'idle' });
              },
              onDrop() {
                setDragState({ type: 'idle' });
              }
            })
          );
        }

        return combine(...registrations);
      }, [fieldIndex, fieldId, isPinnedLeadingFreezeBoundary]);

      const shouldShowTelepointer = !isSelected;
      const nameplateRef = useRef<HTMLTableCellElement>(null);
      const [isHovering, setIsHovering] = useState(false);
      const handleMouseEnter = useCallback(() => {
        if (!shouldShowTelepointer) {
          return;
        }
        setIsHovering(true);
      }, [shouldShowTelepointer]);

      const handleMouseLeave = useCallback(() => {
        if (!shouldShowTelepointer) {
          return;
        }
        setIsHovering(false);
      }, [shouldShowTelepointer]);

      const pinnedLeadingStyle = useMemo((): CSSProperties | undefined => {
        return getPinnedLeadingHeaderCellStyle({
          isEnabled: isPinnedLeadingEnabled,
          layout: pinnedLeadingLayout,
          isSelectedFromPing: isFieldSelectedFromPing,
          isSelectionsPerformanceEnabled,
          isFieldDragActive: isDraggingActive ?? false,
          maxPinnedLeadingFieldCount
        });
      }, [
        isFieldSelectedFromPing,
        isPinnedLeadingEnabled,
        isSelectionsPerformanceEnabled,
        isDraggingActive,
        maxPinnedLeadingFieldCount,
        pinnedLeadingLayout
      ]);

      const pinnedLeadingMenuConfig = useMemo(() => {
        if (!isPinnedLeadingEnabled) {
          return undefined;
        }
        return {
          fieldIndex,
          pinnedLeadingFieldCount,
          maxPinnedLeadingFieldCount,
          onPinThroughThisColumn: () => setPinnedLeadingFieldCount(fieldIndex + 1),
          onUnpinAllColumns: () => setPinnedLeadingFieldCount(0),
          onFieldsInserted: ({
            insertBefore,
            selectedFieldIds
          }: {
            insertBefore: boolean;
            selectedFieldIds: ReadonlyArray<string>;
          }) => {
            if (selectedFieldIds.length === 0) {
              return;
            }
            const anchorFieldId = insertBefore
              ? selectedFieldIds[0]
              : selectedFieldIds[selectedFieldIds.length - 1];
            if (!anchorFieldId) {
              return;
            }
            const anchorIndex = visibleFieldIds.indexOf(anchorFieldId);
            if (anchorIndex < 0) {
              return;
            }
            const insertionIndex = insertBefore ? anchorIndex : anchorIndex + 1;
            const nextPinnedLeadingFieldCount = Math.min(
              getPinnedLeadingFieldCountAfterFieldsInserted({
                fieldIds: visibleFieldIds,
                insertionIndex,
                pinnedLeadingFieldCount
              }),
              maxPinnedLeadingFieldCount
            );

            if (nextPinnedLeadingFieldCount !== pinnedLeadingFieldCount) {
              setPinnedLeadingFieldCount(nextPinnedLeadingFieldCount);
            }
          },
          onFieldsRemoved: (fieldIdsToRemove: ReadonlyArray<string>) => {
            const nextPinnedLeadingFieldCount =
              getPinnedLeadingFieldCountAfterFieldsRemoved({
                fieldIds: visibleFieldIds,
                removedFieldIds: fieldIdsToRemove,
                pinnedLeadingFieldCount
              });

            if (nextPinnedLeadingFieldCount !== pinnedLeadingFieldCount) {
              setPinnedLeadingFieldCount(nextPinnedLeadingFieldCount);
            }
          },
          getViewChangesForFieldsHidden: (
            fieldIdsToHide: ReadonlyArray<string>
          ): ViewChangeType[] => {
            const nextPinnedLeadingFieldCount =
              getPinnedLeadingFieldCountAfterFieldsRemoved({
                fieldIds: visibleFieldIds,
                removedFieldIds: fieldIdsToHide,
                pinnedLeadingFieldCount
              });

            if (nextPinnedLeadingFieldCount !== pinnedLeadingFieldCount) {
              return [
                {
                  type: ViewChangeTypes.VIEW_PROPERTIES,
                  value: setPinnedLeadingFieldCountInProperties(
                    view.settings.properties,
                    nextPinnedLeadingFieldCount
                  )
                }
              ];
            }

            return [];
          }
        };
      }, [
        fieldIndex,
        isPinnedLeadingEnabled,
        maxPinnedLeadingFieldCount,
        pinnedLeadingFieldCount,
        setPinnedLeadingFieldCount,
        view.settings.properties,
        visibleFieldIds
      ]);

      const isPinnedLeadingCell = pinnedLeadingStyle !== undefined;
      const isFirstScrollableAfterPinnedLeading =
        isPinnedLeadingEnabled &&
        pinnedLeadingFieldCount > 0 &&
        fieldIndex === pinnedLeadingFieldCount;
      const pinnedLeadingGapCoverStyle =
        isPinnedLeadingCell && fieldIndex > 0
          ? getPinnedLeadingHeaderSurfaceGapCoverStyle()
          : undefined;
      const pinnedLeadingBoundaryShadowStyle =
        isPinnedLeadingFreezeBoundary && visibleTableContentHeight > 0
          ? getPinnedLeadingBoundaryCellShadowStyle()
          : undefined;
      const shouldShowDropIndicator =
        dragState.type === 'is-over' &&
        Boolean(dragState.closestEdge) &&
        !isPinnedLeadingDropIndicatorOccluded({
          cellElement: thRef.current,
          boundaryElement: pinnedLeadingBoundaryElementRef.current,
          closestEdge: dragState.closestEdge,
          isPinnedLeadingEnabled,
          isPinnedLeadingCell,
          isPinnedLeadingFreezeBoundary,
          isFirstScrollableAfterPinnedLeading
        });
      return (
        <Th
          id={fieldId}
          containerHeight={containerHeight}
          isDragOver={isDragOver}
          draggable={canEditFields && isHoverDragButton}
          onDragStart={handleDragStart}
          onDrag={handleDrag}
          onDragEnd={handleDragEnd}
          isFirstInRow={isFirstInRow}
          data-testid="table-layout-header-cell"
          data-field-id={fieldId}
          data-col-index={fieldIndex}
          ref={composeRefs(
            thRef,
            (ref as TableLayoutHeaderCellRefObj)?.current?.headerFieldRef,
            nameplateRef
          )}
          className={classNames([
            className,
            {
              [TABLE_CLASSNAMES.IS_SELECTED]: isLegacySelectedFromRedux,
              [TABLE_CLASSNAMES.IS_SOFT_SELECTED]: isLegacySoftSelected,
              [TABLE_CLASSNAMES.MAXIMIZE_HEIGHT]: isDraggingActive,
              [TABLE_CLASSNAMES.SHOW_AS_DISABLED]: showAsDisabled
            }
          ])}
          isSelectedFromPing={isSelectionsPerformanceEnabled && isFieldSelectedFromPing}
          isFirstOfSelection={isSelectionsPerformanceEnabled && isFieldFirstOfSelection}
          isLastOfSelection={isSelectionsPerformanceEnabled && isFieldLastOfSelection}
          collaboratorColor={recentCollaboratorColor}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClickCapture={handleHeaderClickCapture}
          onContextMenuCapture={handleHeaderContextMenu}
          style={
            isPinnedLeadingEnabled
              ? { zIndex: 0, ...pinnedLeadingStyle }
              : pinnedLeadingStyle
          }
          data-dbs-pinned-leading={pinnedLeadingStyle ? 'true' : undefined}
          data-dbs-pinned-leading-freeze-boundary={
            isPinnedLeadingFreezeBoundary ? 'true' : undefined
          }
        >
          {pinnedLeadingGapCoverStyle && (
            <div
              data-testid="pinned-leading-surface-gap-cover"
              style={pinnedLeadingGapCoverStyle}
            />
          )}
          {pinnedLeadingBoundaryShadowStyle && (
            <div
              data-testid="pinned-leading-freeze-boundary-shadow"
              style={pinnedLeadingBoundaryShadowStyle}
            />
          )}
          {isPinnedLeadingFreezeBoundary && (
            <div
              ref={pinnedLeadingBoundaryDropTargetRef}
              data-testid="pinned-leading-freeze-boundary-drop-target"
              style={
                isDraggingActive
                  ? PINNED_LEADING_BOUNDARY_DROP_TARGET_ACTIVE_STYLE
                  : PINNED_LEADING_BOUNDARY_DROP_TARGET_INACTIVE_STYLE
              }
            />
          )}
          {isHovering &&
            shouldShowTelepointer &&
            !isAIStreaming &&
            displayParticipants.length > 0 && (
              <PresenceNameplate
                participants={displayParticipants}
                anchorRef={nameplateRef}
                position={'bottom'}
              />
            )}
          {canEditFields && dragState.type === 'idle' && (
            <SelectFieldMenu
              ref={fieldMenuRef}
              contentId={contentId}
              viewSettings={viewSettings}
              currentViewId={currentViewId}
              canEditFields={canEditFields}
              fieldId={fieldId}
              hoverProps={dragButtonHoverProps}
              handleSelectColumn={handleSelectColumn}
              hasUserEditedAiSummaryCells={getHasUserEditedAiSummaryCells}
              onAiSummaryRerunEntireColumn={handleAiSummaryRerunEntireColumn}
              onAiSummaryRerunEmptyEntries={handleAiSummaryRerunEmptyEntries}
              isAiSummaryRerunDisabled={isAiFieldRunActive}
              pinnedLeadingMenu={pinnedLeadingMenuConfig}
              onEditAiSummaryField={handleEditAiSummaryField}
            />
          )}
          <FieldNameWithActions
            contentId={contentId}
            fieldId={fieldId}
            isReadonly={!canEditFields}
            isDragging={showAsDisabled}
            isSelected={isLegacySelectedFromRedux}
            isSoftSelected={isLegacySoftSelected}
            hoverDeleteButton={legacyHoverDeleteButton}
            isLastInRow={isLastInRow}
            selectedAdjacentCells={legacySelectedAdjacentCells}
            ref={inputRef}
            isFirstInRow
            openSettingsPanelOnClick={!isContextualMenusM2Enabled}
            fieldActionComp={
              canEditFields && aiSummaryField ? (
                <AiSummaryFieldHeaderAction
                  confirmationKey={`${contentId}:${fieldId}`}
                  hasUserEditedCells={getHasUserEditedAiSummaryCells}
                  lastRunAt={aiFieldLastRunAt}
                  onRerunEntireColumn={handleAiSummaryRerunEntireColumn}
                  onRerunEmptyEntries={handleAiSummaryRerunEmptyEntries}
                  isDisabled={isAiFieldRunActive}
                />
              ) : undefined
            }
            {...otherProps}
          />
          {shouldShowDropIndicator &&
            dragState.type === 'is-over' &&
            dragState.closestEdge &&
            (dragState.closestEdge === 'right' && isPinnedLeadingFreezeBoundary ? (
              <PragmaticDropIndicatorStylingLastInRow containerHeight={containerHeight} />
            ) : isLastInRow && dragState.closestEdge === 'right' ? (
              <PragmaticDropIndicatorStylingLastInRow containerHeight={containerHeight}>
                <DropIndicator edge={dragState.closestEdge} />
              </PragmaticDropIndicatorStylingLastInRow>
            ) : (
              <PragmaticDropIndicatorStyling containerHeight={containerHeight}>
                <DropIndicator edge={dragState.closestEdge} />
              </PragmaticDropIndicatorStyling>
            ))}
        </Th>
      );
    }
  )
);

export default TableLayoutHeaderCell;
